import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { DeliveryPackage } from "src/api/generated/erp/db/types/tables/deliveryPackage.ts";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import {
	nonNegativeFloatRule,
	requiredNonNegativeIntegerRule,
	requireRule
} from "src/components/common/forms/validation.ts";
import i18n from "i18next";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import {
	DeliveryPackageFormApi,
	DeliveryPackageFormApi_InitData
} from "src/api/generated/erp/delivery/api/deliveryPackageFormApi.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { DeepPartial } from "react-hook-form";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { DeliveryPackageApi } from "src/api/generated/erp/delivery/api/deliveryPackageApi.ts";
import { printFile } from "src/utils/filePrinting.ts";
import { faGear } from "@fortawesome/pro-regular-svg-icons";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackageFormValues extends DeliveryPackage {
	printPackageLabel: boolean;
	printShippingLabel: boolean;
	closePackage: boolean;
}

export interface DeliveryPackageFormProps extends FormCommonProps<number> {
	readonly?: boolean;
	showClosePackage?: boolean;
	executeInsert?: (values: DeliveryPackageFormValues) => Promise<number>;
	initialization:
		| {
				type: "existingPackage";
				deliveryPackageId: number;
		  }
		| {
				type: "newPackage";
				deliveryId?: number | null;
				deliveryPackageSources: DeliveryPackageSource[];
				bundleChildPackageIds?: number[];
				weight?: number;
		  };
}

export const DeliveryPackageForm = (props: DeliveryPackageFormProps) => {
	const { initialization } = props;
	return (
		<AsyncFetchRender
			fetch={() =>
				DeliveryPackageFormApi.getInitData({
					deliveryPackageId:
						initialization.type === "existingPackage" ? initialization.deliveryPackageId : undefined,
					source:
						initialization.type === "existingPackage" ?
							undefined
						:	initialization.deliveryPackageSources[0],
					isBundle:
						initialization.type === "existingPackage" ?
							undefined
						:	initialization.bundleChildPackageIds != null,
				})
			}
			content={(initData) => <DeliveryPackageFormContent initData={initData} {...props} />}
		/>
	);
};

interface DeliveryPackageFormContentProps extends DeliveryPackageFormProps {
	initData: DeliveryPackageFormApi_InitData;
}

const DeliveryPackageFormContent = ({
	initData,
	onFormEdited,
	onCompleted,
	readonly,
	initialization,
	showClosePackage,
	executeInsert,
}: DeliveryPackageFormContentProps) => {
	const isNewBundle = initialization.type === "newPackage" && initialization.bundleChildPackageIds != null;

	const defaultPackageTypeId =
		initData.packageTypeOptions.find((packageType) => packageType.isDefault)?.deliveryPackageTypeId ??
		genericNullableValue<number>();
	const defaultWarehouseLocationId =
		initData.warehouseLocationOptions.find((location) => location.isDefaultShippingWarehouseLocation)
			?.warehouseLocationId ?? genericNullableValue<number>();

	const [openContextMenu, contextMenuState] = useContextMenu();

	const storageKeySuffix = isNewBundle ? "_bundle" : "";
	const [storedDefaultValues, setStoredDefaultValues] = useStoredState({
		defaultValue: {
			packageTypeId: defaultPackageTypeId,
			warehouseLocationId: defaultWarehouseLocationId,
			description: "",
		},
		key: `6B932BA61962B645${storageKeySuffix}`,
	});
	const [rememberDescription, setRememberDescription] = useStoredState({
		defaultValue: false as boolean,
		key: `7C0A4D2E91588F1B${storageKeySuffix}`,
		type: "boolean",
	});

	return (
		<>
			<AavoForm<DeliveryPackageFormValues, number>
				defaultValues={getDefaultValues()}
				submit={submit}
				columns={2}
				onFormEdited={onFormEdited}
				onCompleted={onCompleted}
				useFormProps={{ disabled: readonly }}
				agentContext={{
					key: defineAgentUiContextKey("erp.delivery.deliveryPackage.form"),
					title: i18n.t("package"),
					pathSegments: [i18n.t("form")],
					data: {
						deliveryPackageId:
							initialization.type === "existingPackage" ? initialization.deliveryPackageId : null,
						deliveryId: initialization.type === "newPackage" ? initialization.deliveryId ?? null : null,
					},
				}}
				footerStartComponents={
					initialization.type === "newPackage" ?
						<AavoIconButton
							icon={faGear}
							size={"small"}
							tooltip={i18n.t("form_options")}
							onClick={(e) => {
								openContextMenu({
									mouseEvent: e,
									content: [
										<MenuCheckbox
											key={"rememberDescription"}
											label={i18n.t("remember_package_description")}
											defaultChecked={rememberDescription}
											onChange={(value) => {
												setRememberDescription(value);
											}}
										/>,
									],
								});
							}}
						/>
					:	undefined
				}
				render={({ control, setValue }) => (
					<>
						<FormTextField
							control={control}
							name={"packageNo"}
							rules={requiredNonNegativeIntegerRule()}
							label={i18n.t("package_no")}
						/>
						<FormTextField control={control} name={"description"} label={i18n.t("description")} autoFocus />
						<FormSelectField
							control={control}
							name={"packageTypeId"}
							label={i18n.t("type")}
							options={initData.packageTypeOptions}
							getOptionKey={(o) => o.deliveryPackageTypeId}
							getOptionLabel={(o) => o.name}
							rules={requireRule()}
							onChange={(_, option) => {
								setValue("length", option?.defaultLength ?? null);
								setValue("width", option?.defaultWidth ?? null);
								setValue("height", option?.defaultHeight ?? null);
								setValue("weight", option?.packageBaseWeight ?? null);
							}}
						/>
						<FormSelectField
							control={control}
							name={"warehouseLocationId"}
							label={i18n.t("shipping_warehouse_location")}
							options={initData.warehouseLocationOptions}
							getOptionKey={(o) => o.warehouseLocationId}
							getOptionLabel={(o) => o.locationCode}
							rules={requireRule()}
						/>
						<FormTextField
							control={control}
							name={"weight"}
							label={i18n.t("weight")}
							rules={nonNegativeFloatRule()}
						/>
						<FormTextField
							control={control}
							name={"length"}
							label={i18n.t("length_cm")}
							rules={nonNegativeFloatRule()}
						/>
						<FormTextField
							control={control}
							name={"width"}
							label={i18n.t("width_cm")}
							rules={nonNegativeFloatRule()}
						/>
						<FormTextField
							control={control}
							name={"height"}
							label={i18n.t("height_cm")}
							rules={nonNegativeFloatRule()}
						/>
						{initialization.type === "newPackage" && (
							<>
								{showClosePackage && (
									<FormCheckbox
										control={control}
										name={"closePackage"}
										label={i18n.t("close_package")}
										spanGridColumns
									/>
								)}
								<FormCheckbox
									control={control}
									name={"printPackageLabel"}
									label={i18n.t("print_package_label")}
									spanGridColumns
								/>
								<FormCheckbox
									control={control}
									name={"printShippingLabel"}
									label={i18n.t("print_shipping_label")}
									spanGridColumns
								/>
							</>
						)}
					</>
				)}
			/>
			<AavoContextMenu state={contextMenuState} />
		</>
	);

	function getDefaultValues(): DeepPartial<DeliveryPackageFormValues> {
		switch (initialization.type) {
			case "existingPackage": {
				return {
					...(initData.deliveryPackage ?? {}),
					printPackageLabel: false,
					printShippingLabel: false,
					closePackage: false,
				};
			}
			case "newPackage": {
				return {
					packageNo: initData.nextPackageNumber,
					warehouseLocationId:
						storedDefaultValues.warehouseLocationId ??
						defaultWarehouseLocationId ??
						initData.warehouseLocationOptions[0]?.warehouseLocationId,
					packageTypeId: storedDefaultValues.packageTypeId ?? undefined,
					description: rememberDescription ? storedDefaultValues.description : "",
					isBundle: isNewBundle,
					weight: initialization.weight,
					printPackageLabel: false,
					printShippingLabel: false,
					closePackage: true,
				};
			}
		}
	}

	async function submit(values: DeliveryPackageFormValues): Promise<number> {
		const { printPackageLabel, printShippingLabel, closePackage: _closePackage, ...deliveryPackage } = values;
		setStoredDefaultValues({
			warehouseLocationId: deliveryPackage.warehouseLocationId,
			packageTypeId: deliveryPackage.packageTypeId,
			description: deliveryPackage.description,
		});

		let deliveryPackageIdNotNull;
		if (initialization.type === "newPackage") {
			if (executeInsert != null) {
				deliveryPackageIdNotNull = await executeInsert(values);
			} else {
				deliveryPackageIdNotNull = await DeliveryPackageFormApi.insert({
					deliveryPackage: deliveryPackage,
					connectToDeliveryId: initialization.deliveryId,
					bundleChildPackageIds: initialization.bundleChildPackageIds,
					connectToSources: initialization.deliveryPackageSources,
				});
			}
		} else {
			await DeliveryPackageFormApi.update({
				deliveryPackage: deliveryPackage,
			});
			deliveryPackageIdNotNull = initialization.deliveryPackageId;
		}

		if (printPackageLabel) {
			const fileHandle = await DeliveryPackageApi.printDeliveryPackageLabel({
				deliveryPackageId: deliveryPackageIdNotNull,
			});
			printFile(fileHandle);
		}
		if (printShippingLabel) {
			const fileHandle = await DeliveryPackageApi.printDeliveryPackageShippingLabels({
				deliveryPackageIds: [deliveryPackageIdNotNull],
			});
			printFile(fileHandle);
		}
		return deliveryPackageIdNotNull;
	}
};
